import { format, parse } from "date-fns";
import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import {
  deleteUnencryptedValue,
  getUnencryptedBooleanValue,
  getUnencryptedStringValue,
  saveUnencryptedBooleanValue,
  saveUnencryptedStringValue,
  UnencryptedStorageKey,
} from "@/hooks/useUnencryptedStorage";
import type { RecommendedGroupClassStorageKey } from "@/hooks/useUnencryptedStorage";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

enum DismissalMode {
  NoneOfTheseTimes = "noneOfTheseTimes",
  RemindMeLater = "remindMeLater",
}

type StorageKeyArgs = {
  appUserId: string;
  patientExternalId: string;
};

type RecommendedGroupClassStorageKeyParams = {
  appUserId?: string;
  patientExternalId?: string;
};

type SetRemindMeLaterParams = {
  appUserId?: string;
  date: Date;
  patientExternalId?: string;
};

type SetNoneOfTheseTimesParams = {
  appUserId?: string;
  isEnabled: boolean;
  patientExternalId?: string;
};

const joinStorageKey = ({
  appUserId,
  patientExternalId,
  prefix,
}: {
  appUserId: string;
  patientExternalId: string;
  prefix:
    | UnencryptedStorageKey.RecommendedGroupClassNoneOfTheseTimes
    | UnencryptedStorageKey.RecommendedGroupClassRemindMeLaterDate;
}): RecommendedGroupClassStorageKey =>
  `${prefix}_${appUserId}_${patientExternalId}`;

const getStorageKeyArgs = (
  params: RecommendedGroupClassStorageKeyParams,
): null | StorageKeyArgs => {
  const { appUserId, patientExternalId } = params;
  if (!isNotNilNotEmpty(appUserId) || !isNotNilNotEmpty(patientExternalId))
    return null;
  return { appUserId, patientExternalId };
};

const buildRemindMeLaterKey = ({
  appUserId,
  patientExternalId,
}: StorageKeyArgs): RecommendedGroupClassStorageKey =>
  joinStorageKey({
    appUserId,
    patientExternalId,
    prefix: UnencryptedStorageKey.RecommendedGroupClassRemindMeLaterDate,
  });

const buildNoneOfTheseTimesKey = ({
  appUserId,
  patientExternalId,
}: StorageKeyArgs): RecommendedGroupClassStorageKey =>
  joinStorageKey({
    appUserId,
    patientExternalId,
    prefix: UnencryptedStorageKey.RecommendedGroupClassNoneOfTheseTimes,
  });

const getRecommendedGroupClassRemindMeLaterDate = (
  params: RecommendedGroupClassStorageKeyParams,
): Date | null => {
  const keyArgs = getStorageKeyArgs(params);
  if (!keyArgs) return null;

  const storageKey = buildRemindMeLaterKey(keyArgs);
  const storedValue = getUnencryptedStringValue(storageKey);
  if (isNil(storedValue)) return null;

  const parsed = parse(storedValue, DateFormat.yearMonthDay, new Date());
  // Guard against invalid data in storage
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const setRecommendedGroupClassRemindMeLaterDate = ({
  appUserId,
  date,
  patientExternalId,
}: SetRemindMeLaterParams): void => {
  const keyArgs = getStorageKeyArgs({ appUserId, patientExternalId });
  if (!keyArgs) return;

  const storageKey = buildRemindMeLaterKey(keyArgs);
  const formattedDate = format(date, DateFormat.yearMonthDay);
  saveUnencryptedStringValue({
    key: storageKey,
    value: formattedDate,
  });
};

const getRecommendedGroupClassNoneOfTheseTimes = (
  params: RecommendedGroupClassStorageKeyParams,
): boolean => {
  const keyArgs = getStorageKeyArgs(params);
  if (!keyArgs) return false;

  const storageKey = buildNoneOfTheseTimesKey(keyArgs);
  return getUnencryptedBooleanValue(storageKey);
};

const setRecommendedGroupClassNoneOfTheseTimes = ({
  appUserId,
  isEnabled,
  patientExternalId,
}: SetNoneOfTheseTimesParams): void => {
  const keyArgs = getStorageKeyArgs({ appUserId, patientExternalId });
  if (!keyArgs) return;

  const storageKey = buildNoneOfTheseTimesKey(keyArgs);
  saveUnencryptedBooleanValue({
    isTrue: isEnabled,
    key: storageKey,
  });
};

const clearRecommendedGroupClassStorage = ({
  appUserId,
  patientExternalId,
}: RecommendedGroupClassStorageKeyParams): void => {
  const keyArgs = getStorageKeyArgs({ appUserId, patientExternalId });
  if (!keyArgs) return;

  const remindKey = buildRemindMeLaterKey(keyArgs);
  const noneKey = buildNoneOfTheseTimesKey(keyArgs);

  deleteUnencryptedValue(remindKey);
  deleteUnencryptedValue(noneKey);
};
/**
 * Returns the dismissal mode for a recommended group class based on MMKV value.
 * If a remind-me-later date exists, returns NoneOfTheseTimes, else RemindMeLater.
 */
const getDismissalMode = ({
  appUserId,
  patientExternalId,
}: {
  appUserId?: string;
  patientExternalId?: string;
}): DismissalMode => {
  const existingDate = getRecommendedGroupClassRemindMeLaterDate({
    appUserId,
    patientExternalId,
  });
  return existingDate && !isNil(existingDate)
    ? DismissalMode.NoneOfTheseTimes
    : DismissalMode.RemindMeLater;
};

export type {
  RecommendedGroupClassStorageKeyParams,
  SetNoneOfTheseTimesParams,
  SetRemindMeLaterParams,
};
export {
  clearRecommendedGroupClassStorage,
  DismissalMode,
  getDismissalMode,
  getRecommendedGroupClassNoneOfTheseTimes,
  getRecommendedGroupClassRemindMeLaterDate,
  setRecommendedGroupClassNoneOfTheseTimes,
  setRecommendedGroupClassRemindMeLaterDate,
};
